import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "./context/CartContext";
import { AdminProvider } from "./context/AdminContext";
import { SiteProvider } from "./context/SiteContext";
import { UserProvider } from "./context/UserContext";
import { Toaster } from "./components/ui/toaster";

// Layout
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Pages
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductPage from "./pages/ProductPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AccountPage from "./pages/AccountPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import ReturnsPolicyPage from "./pages/ReturnsPolicyPage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import OrderDetailPage from "./pages/admin/OrderDetailPage";

// Layout wrapper for public pages
const PublicLayout = ({ children }) => (
  <>
    <Header />
    <main className="min-h-screen">{children}</main>
    <Footer />
  </>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

function App() {
  return (
    <SiteProvider>
      <UserProvider>
        <AdminProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/"
                  element={
                    <PublicLayout>
                      <HomePage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <PublicLayout>
                      <ProductsPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/products/:slug"
                  element={
                    <PublicLayout>
                      <ProductPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <PublicLayout>
                      <CheckoutPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/order-confirmation"
                  element={
                    <PublicLayout>
                      <OrderConfirmationPage />
                    </PublicLayout>
                  }
                />

                {/* Blog Routes */}
                <Route
                  path="/blog"
                  element={
                    <PublicLayout>
                      <BlogPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/blog/:slug"
                  element={
                    <PublicLayout>
                      <BlogPostPage />
                    </PublicLayout>
                  }
                />

                {/* Policy Pages */}
                <Route
                  path="/shipping-policy"
                  element={
                    <PublicLayout>
                      <ShippingPolicyPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/returns-policy"
                  element={
                    <PublicLayout>
                      <ReturnsPolicyPage />
                    </PublicLayout>
                  }
                />

                {/* Auth Routes */}
                <Route
                  path="/login"
                  element={
                    <PublicLayout>
                      <LoginPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <PublicLayout>
                      <RegisterPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <PublicLayout>
                      <AccountPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/account/*"
                  element={
                    <PublicLayout>
                      <AccountPage />
                    </PublicLayout>
                  }
                />

                {/* Password Reset Routes */}
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route
                  path="/reset-password"
                  element={<ResetPasswordPage />}
                />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/admin/orders/:orderId" element={<OrderDetailPage />} />
              </Routes>
              <Toaster />
            </BrowserRouter>
          </CartProvider>
        </AdminProvider>
      </UserProvider>
    </SiteProvider>
  );
}

export default App;
